import { formatDateNumber } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { VisitNote } from '@seen/core/models/visitNote'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { DotSeparator } from '@seen/ui'
import type { JSX } from 'react'
import { useMemo, useState } from 'react'
import { Sheet, SheetContent } from '#src/components/core/index.ts'
import { DocumentReferenceDisplay } from '#src/components/document-reference/DocumentReferenceDisplay.tsx'
import { useSearch } from '#src/hooks/index.ts'

interface VisitNoteDocumentsWidgetProps {
  visitNote: VisitNote
  patientId: string
}

export function VisitNoteDocumentsWidget({
  visitNote,
  patientId,
}: VisitNoteDocumentsWidgetProps): JSX.Element | null {
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)

  const documentEntries = useMemo(() => {
    return Object.values(visitNote.sections)
      .flat()
      .filter((s) => VisitNote.isDocumentSection(s))
      .flatMap((s) => s.entry ?? [])
      .filter((e) => !!referenceDisplay(e))
  }, [visitNote])

  const docIdString = useMemo(
    () =>
      documentEntries
        .filter((e) => isOfType(e, 'DocumentReference'))
        .map((r) => resolveId(r))
        .filter(Boolean)
        .join(','),
    [documentEntries]
  )

  const docIds = useMemo(
    () => (docIdString ? docIdString.split(',') : []),
    [docIdString]
  )

  const { data: docRefs } = useSearch(
    ExtendedDocumentReference,
    docIds.length > 0
      ? { _id: docIds, _count: docIds.length, _sort: '-_lastUpdated' }
      : undefined
  )

  const dateById = useMemo(() => {
    const map = new Map<string, string>()
    if (!docRefs) {
      return map
    }
    for (const doc of docRefs) {
      if (doc.id && doc.dateString) {
        map.set(doc.id, doc.dateString)
      }
    }
    return map
  }, [docRefs])

  if (documentEntries.length === 0) {
    return null
  }

  return (
    <>
      <div className="rounded-md border">
        <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
          <h4 className="text-sm font-medium">Documents</h4>
          <span className="text-xs text-muted-foreground">
            {documentEntries.length}
          </span>
        </div>
        <div className="flex flex-col gap-1 px-3 py-2 text-sm">
          {documentEntries.map((doc, idx) => {
            const docId = isOfType(doc, 'DocumentReference')
              ? resolveId(doc)
              : null
            const date = docId ? dateById.get(docId) : undefined
            return (
              <p key={docId ? `${docId}-${idx}` : idx}>
                {docId ? (
                  <button
                    type="button"
                    className="text-left underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground/60"
                    onClick={() => setSelectedDocId(docId)}
                  >
                    {referenceDisplay(doc)}
                  </button>
                ) : (
                  <span>{referenceDisplay(doc)}</span>
                )}
                {date && (
                  <span className="select-none">
                    <DotSeparator />
                    <span className="text-foreground-tertiary">
                      {formatDateNumber(date)}
                    </span>
                  </span>
                )}
              </p>
            )
          })}
        </div>
      </div>
      <Sheet
        open={!!selectedDocId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedDocId(null)
          }
        }}
      >
        <SheetContent className="w-[85%] max-w-xl p-5">
          {selectedDocId && (
            <DocumentReferenceDisplay
              documentReference={{
                reference: `DocumentReference/${selectedDocId}`,
              }}
              onDelete={() => setSelectedDocId(null)}
              hideParticipant
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}
